import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { type CardDisputeDocumentEvidenceInput, CardDisputeEvidenceType } from '@column/api';
import { Button, Icon } from '@column/column-ui-kit';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@column/ui';

import { useUploadCardDisputeDocument } from '~/lib/api/cardDisputes';
import { CARD_DISPUTE_EVIDENCE_TYPE_LABELS } from '~/lib/disputes/labels';
import { useSessionStore } from '~/stores/Session';
import { convertByteToString } from '~/util';

import {
  CARD_DISPUTE_EVIDENCE_ACCEPT,
  getEvidenceErrorMessage,
  removeEvidenceValue,
  upsertEvidenceValue,
  validateCardDisputeEvidenceFile,
} from './evidence';

export interface DisputeEvidenceComposerProps {
  value: CardDisputeDocumentEvidenceInput[];
  onChange: (value: CardDisputeDocumentEvidenceInput[]) => void;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
}

type UploadStatus = 'uploading' | 'uploaded' | 'error';

interface ComposerItem {
  localId: string;
  idempotencyKey: string;
  file?: File;
  fileName: string;
  size?: number;
  evidenceType?: CardDisputeEvidenceType;
  documentId?: string;
  status: UploadStatus;
  error?: string;
  retryable: boolean;
  trackedByValue: boolean;
}

const evidenceTypes = Object.values(CardDisputeEvidenceType);

const referenceItem = (evidence: CardDisputeDocumentEvidenceInput): ComposerItem => ({
  localId: `document-${evidence.document_id}`,
  idempotencyKey: uuid(),
  fileName: evidence.document_id,
  evidenceType: evidence.evidence_type,
  documentId: evidence.document_id,
  status: 'uploaded',
  retryable: false,
  trackedByValue: true,
});

export const DisputeEvidenceComposer: React.FC<DisputeEvidenceComposerProps> = ({
  value,
  onChange,
  disabled = false,
  onBusyChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<ComposerItem[]>(value.map(referenceItem));
  const activeItemIdsRef = useRef(new Set(itemsRef.current.map((item) => item.localId)));
  const mountedRef = useRef(true);
  const valueRef = useRef(value);
  valueRef.current = value;

  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const contextKey = `${platformId ?? ''}:${isSandbox ? 'sandbox' : 'live'}`;
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;
  const previousContextKeyRef = useRef(contextKey);

  const [items, setItems] = useState<ComposerItem[]>(itemsRef.current);
  const uploadMutation = useUploadCardDisputeDocument();
  const valueKey = value.map((entry) => `${entry.document_id}:${entry.evidence_type}`).join('|');

  const updateItems = useCallback((update: (current: ComposerItem[]) => ComposerItem[]) => {
    const next = update(itemsRef.current);
    itemsRef.current = next;
    setItems(next);
    return next;
  }, []);

  const updateValue = useCallback(
    (next: CardDisputeDocumentEvidenceInput[]) => {
      valueRef.current = next;
      onChange(next);
    },
    [onChange]
  );

  useEffect(() => {
    const externalValues = new Map(valueRef.current.map((entry) => [entry.document_id, entry]));
    updateItems((current) => {
      const next = current.flatMap((item) => {
        if (!item.documentId || !item.trackedByValue) {
          return [item];
        }
        const external = externalValues.get(item.documentId);
        if (!external) {
          activeItemIdsRef.current.delete(item.localId);
          return [];
        }
        externalValues.delete(item.documentId);
        return [{ ...item, evidenceType: external.evidence_type }];
      });
      externalValues.forEach((entry) => {
        const item = referenceItem(entry);
        activeItemIdsRef.current.add(item.localId);
        next.push(item);
      });
      return next;
    });
  }, [valueKey, updateItems]);

  useEffect(() => {
    if (previousContextKeyRef.current === contextKey) {
      return;
    }
    previousContextKeyRef.current = contextKey;
    activeItemIdsRef.current.clear();
    updateItems(() => []);
    updateValue([]);
  }, [contextKey, updateItems, updateValue]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      activeItemIdsRef.current.clear();
      onBusyChange?.(false);
    };
  }, [onBusyChange]);

  const busy = useMemo(() => items.some((item) => item.status === 'uploading'), [items]);

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const upload = useCallback(
    async (localId: string, uploadContextKey: string) => {
      const item = itemsRef.current.find((entry) => entry.localId === localId);
      if (!item?.file) {
        return;
      }

      try {
        const document = await uploadMutation.mutateAsync({
          file: item.file,
          idempotencyKey: item.idempotencyKey,
        });

        if (
          !mountedRef.current ||
          !activeItemIdsRef.current.has(localId) ||
          contextKeyRef.current !== uploadContextKey
        ) {
          return;
        }

        const nextItems = updateItems((current) =>
          current.map((entry) =>
            entry.localId === localId
              ? {
                  ...entry,
                  documentId: document.id,
                  file: undefined,
                  fileName: document.description || entry.fileName,
                  size: document.size,
                  status: 'uploaded',
                  error: undefined,
                  retryable: false,
                  trackedByValue: entry.trackedByValue || Boolean(entry.evidenceType),
                }
              : entry
          )
        );
        const uploadedItem = nextItems.find((entry) => entry.localId === localId);
        if (uploadedItem?.documentId && uploadedItem.evidenceType) {
          updateValue(
            upsertEvidenceValue(valueRef.current, {
              document_id: uploadedItem.documentId,
              evidence_type: uploadedItem.evidenceType,
            })
          );
        }
      } catch (error) {
        if (
          !mountedRef.current ||
          !activeItemIdsRef.current.has(localId) ||
          contextKeyRef.current !== uploadContextKey
        ) {
          return;
        }
        updateItems((current) =>
          current.map((entry) =>
            entry.localId === localId
              ? {
                  ...entry,
                  status: 'error',
                  error: getEvidenceErrorMessage(error, `${entry.fileName}: upload failed.`),
                  retryable: true,
                }
              : entry
          )
        );
      }
    },
    [updateItems, updateValue, uploadMutation]
  );

  const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (disabled || selectedFiles.length === 0) {
      return;
    }

    const newItems = selectedFiles.map<ComposerItem>((file) => {
      const validationError = validateCardDisputeEvidenceFile(file);
      return {
        localId: uuid(),
        idempotencyKey: uuid(),
        file,
        fileName: file.name,
        size: file.size,
        status: validationError ? 'error' : 'uploading',
        error: validationError,
        retryable: false,
        trackedByValue: false,
      };
    });

    newItems.forEach((item) => activeItemIdsRef.current.add(item.localId));
    updateItems((current) => [...current, ...newItems]);
    newItems.filter((item) => item.status === 'uploading').forEach((item) => void upload(item.localId, contextKey));
  };

  const handleEvidenceType = (localId: string, evidenceType: CardDisputeEvidenceType) => {
    const nextItems = updateItems((current) =>
      current.map((item) =>
        item.localId === localId
          ? { ...item, evidenceType, trackedByValue: item.trackedByValue || Boolean(item.documentId) }
          : item
      )
    );
    const selectedItem = nextItems.find((entry) => entry.localId === localId);
    if (selectedItem?.documentId) {
      updateValue(
        upsertEvidenceValue(valueRef.current, {
          document_id: selectedItem.documentId,
          evidence_type: evidenceType,
        })
      );
    }
  };

  const handleRemove = (item: ComposerItem) => {
    activeItemIdsRef.current.delete(item.localId);
    updateItems((current) => current.filter((entry) => entry.localId !== item.localId));
    if (item.documentId) {
      updateValue(removeEvidenceValue(valueRef.current, item.documentId));
    }
  };

  const handleRetry = (item: ComposerItem) => {
    if (!item.file || disabled) {
      return;
    }
    updateItems((current) =>
      current.map((entry) =>
        entry.localId === item.localId ? { ...entry, status: 'uploading', error: undefined } : entry
      )
    );
    void upload(item.localId, contextKey);
  };

  return (
    <div className="flex flex-col gap-3" data-dd-privacy="hidden" data-testid="dispute-evidence-composer">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">Evidence documents</p>
          <p className="mt-1 text-xs text-muted-foreground">
            PDF up to 2 MiB; JPEG or TIFF up to 10 MiB. PNG is not supported.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="small"
          icon={<Icon.Plus />}
          isDisabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Add files
        </Button>
        <input
          ref={inputRef}
          className="sr-only"
          type="file"
          multiple
          accept={CARD_DISPUTE_EVIDENCE_ACCEPT}
          aria-label="Choose dispute evidence files"
          data-testid="dispute-evidence-file-input"
          disabled={disabled}
          onChange={handleFiles}
        />
      </div>

      {items.length > 0 && (
        <div className="flex flex-col gap-2">
          {items.map((item) => (
            <div key={item.localId} className="rounded-lg border border-border bg-card p-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex min-w-0 items-start gap-2">
                  <Icon.Document />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{item.fileName}</p>
                    {item.size !== undefined && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{convertByteToString(item.size)}</p>
                    )}
                  </div>
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-2 lg:max-w-md">
                  <Select
                    value={item.evidenceType}
                    disabled={disabled}
                    onValueChange={(nextValue) =>
                      handleEvidenceType(item.localId, nextValue as CardDisputeEvidenceType)
                    }
                  >
                    <SelectTrigger className="w-full" aria-label={`Evidence category for ${item.fileName}`}>
                      <SelectValue placeholder="Select evidence category" />
                    </SelectTrigger>
                    <SelectContent position="popper" align="end">
                      {evidenceTypes.map((evidenceType) => (
                        <SelectItem key={evidenceType} value={evidenceType}>
                          {CARD_DISPUTE_EVIDENCE_TYPE_LABELS[evidenceType]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p
                      className={`text-xs ${item.status === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}
                      data-testid={`upload-state-${item.localId}`}
                    >
                      {item.status === 'uploading' && 'Uploading'}
                      {item.status === 'uploaded' && 'Uploaded; not yet attached'}
                      {item.status === 'error' && item.error}
                    </p>
                    <div className="flex items-center gap-2">
                      {item.status === 'error' && item.retryable && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="small"
                          icon={<Icon.Reset />}
                          isDisabled={disabled}
                          onClick={() => handleRetry(item)}
                        >
                          Retry
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="subtle"
                        size="small"
                        icon={<Icon.Cross />}
                        isDisabled={disabled}
                        onClick={() => handleRemove(item)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
